<template>
    <div class="container mx-auto">
        <!-- Introduction -->
        <ProductIntroSection
            :subheading="$t('products.altcoin.intro.subheading')"
            :heading="$t('products.altcoin.intro.heading')"
            :features="introFeatures"
            :product-group="productGroup"
            :selected-product-variant="selectedProductVariant"
            :sorted-product-variants="sortedProductVariants"
            :pricing-title="$t('products.altcoin.intro.pricingTitle')"
            :pricing-subtitle="$t('products.altcoin.intro.pricingSubtitle')"
            :cta-text="$t('products.altcoin.intro.ctaText')"
            :cta-subtext="$t('products.altcoin.intro.ctaSubtext')"
            @select-product="$emit('select-product', $event)"
            @add-to-cart="$emit('add-to-cart')"
            @add-webinar-to-cart="$emit('add-webinar-to-cart')"
        />

        <!-- So funktioniert's -->
        <HowItWorksSection
            :title="$t('products.altcoin.howItWorks.title')"
            :description="howItWorksDescription"
            :features-title="$t('products.altcoin.howItWorks.featuresTitle')"
            :feature-cards="howItWorksCards"
        />

        <!-- Altcoin Liste -->
        <ProductListSection :title="$t('products.altcoin.list.title')" :items="altcoinData.info" />

        <!-- Mehr Tiefe durch fokussierte Videoanalysen -->
        <section class="mb-20 lg:mb-0 p-5 sm:p-10 lg:p-20 bg-slate-50">
            <div class="max-w-6xl mx-auto">
                <h2 class="text-2xl lg:text-3xl font-extrabold text-slate-900 leading-snug mb-6">
                    <i class="fi fi-rr-video-camera mr-2" />
                    {{ $t('products.altcoin.videoDeepDive.title') }}
                </h2>
                <p class="text-slate-600 mb-4">
                    {{ $t('products.altcoin.videoDeepDive.p1') }}
                </p>
                <p class="text-slate-600">
                    {{ $t('products.altcoin.videoDeepDive.p2') }}
                </p>
            </div>
        </section>

        <!-- Zusätzliche Videoanalyse Info -->
        <section class="mb-20 lg:mb-0 p-5 sm:p-10 lg:p-20 bg-gradient-to-b from-gray-100 to-gray-200 rounded-lg shadow-inner">
            <div class="max-w-6xl mx-auto">
                <h2 class="text-2xl lg:text-3xl font-extrabold text-slate-900 text-center mb-8">
                    {{ $t('products.altcoin.videoExtra.title') }}
                </h2>
                <div class="grid md:grid-cols-2 gap-6">
                    <div class="bg-white rounded-lg p-6 shadow-md">
                        <h3 class="text-xl font-light mb-3 text-center">
                            <i class="fi fi-rr-video-camera mr-2" /><span class="font-bold">{{ $t('products.altcoin.videoExtra.cards.dogecoin.name') }}</span
                            ><br />
                            <div class="flex flex-row items-center justify-center">
                                <img v-if="dogeLogo" :src="dogeLogo" class="w-8 h-8 mr-2" alt="" />{{ $t('products.altcoin.videoExtra.cards.dogecoin.symbol') }}
                            </div>
                        </h3>
                        <p class="text-slate-600 text-sm">
                            {{ $t('products.altcoin.videoExtra.cards.dogecoin.text') }}
                        </p>
                    </div>
                    <div class="bg-white rounded-lg p-6 shadow-md">
                        <h3 class="text-xl font-light mb-3 text-center">
                            <i class="fi fi-rr-video-camera mr-2" /><span class="font-bold">{{ $t('products.altcoin.videoExtra.cards.slp.name') }}</span
                            ><br />
                            <div class="flex flex-row items-center justify-center">
                                <img v-if="slpLogo" :src="slpLogo" class="w-8 h-8 mr-2" alt="" />
                                {{ $t('products.altcoin.videoExtra.cards.slp.symbol') }}
                            </div>
                        </h3>
                        <p class="text-slate-600 text-sm">
                            {{ $t('products.altcoin.videoExtra.cards.slp.text') }}
                        </p>
                    </div>
                </div>
            </div>
        </section>

        <!-- Shopify -->
        <section
            v-if="productGroup.meta?.shopifyProductIds?.length > 0"
            class="lg:bg-gray-200 p-5 sm:p-10 lg:p-20 mb-20 lg:mb-0 bg-gradient-to-b from-gray-100 to-gray-200 rounded-lg shadow-inner"
        >
            <h2 class="text-center mb-4">{{ $t('products.altcoin.shopify.title') }}</h2>
            <div class="gap-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 place-self-center justify-items-stretch">
                <div v-for="shopifyProductId in productGroup.meta?.shopifyProductIds" :key="shopifyProductId" class="w-full h-full flex">
                    <ShopifyProduct :product-id="shopifyProductId" bg-color="bg-white" class="h-full" />
                </div>
            </div>
        </section>

        <!-- FAQ -->
        <section class="mb-20 lg:mb-0 p-5 sm:p-10 lg:p-20">
            <strong data-aos="fade-right">
                <h3 class="px-4 pt-10 text-center">{{ $t('products.altcoin.faq.heading') }}</h3>
            </strong>
            <div>
                <div v-for="(faq, key) in faqs" :key="key" data-aos="fade-up">
                    <Accordeon :title="faq.question" :content="faq.answer" />
                </div>
            </div>
        </section>

        <!-- Das bietet unsere Analyse -->
        <ProductAnalysisSection
            :title="$t('products.altcoin.analysis.title')"
            :description="analysisDescription"
            :features="analysisFeatures"
            :image-src="$asset('/hkcm/images/unsereAnalyse.png')"
            :image-alt="$t('products.altcoin.analysis.imageAlt')"
        />

        <!-- SEO Content -->
        <section>
            <div class="mx-auto px-0 sm:px-6 lg:px-8 py-14 prose prose-slate max-w-none">
                <!-- Unsere Altcoin-Analysestrategie -->
                <div class="mb-20 lg:mb-0 p-5 sm:p-10 lg:p-20 flex flex-col-reverse lg:flex-row items-center gap-8">
                    <div class="lg:w-1/2 m-auto justify-center">
                        <h2>{{ $t('products.altcoin.seo.strategy.title') }}</h2>
                        <p class="text-slate-600" v-html="$t('products.altcoin.seo.strategy.text')"></p>
                    </div>
                    <div class="lg:w-1/2 m-auto flex justify-center">
                        <img :src="$asset('/hkcm/images/altcoin_strategy.jpg')" :alt="$t('products.altcoin.seo.strategy.imageAlt')" class="w-auto rounded-lg shadow-lg" />
                    </div>
                </div>

                <!-- Was sind Altcoins -->
                <div
                    class="bg-gradient-to-b from-gray-100 to-gray-200 rounded-lg shadow-inner mb-20 lg:mb-0 p-5 sm:p-10 lg:p-20 flex flex-col-reverse lg:flex-row-reverse items-center gap-8"
                >
                    <div class="lg:w-1/2 m-auto flex-col justify-center">
                        <h2>{{ $t('products.altcoin.seo.whatAre.title') }}</h2>
                        <p class="text-slate-600" v-html="$t('products.altcoin.seo.whatAre.text')"></p>
                    </div>
                    <div class="lg:w-1/2 m-auto flex justify-center">
                        <a :href="$asset('/hkcm/images/altcoin_info.png')" target="_blank" class="cursor-pointer">
                            <img :src="$asset('/hkcm/images/altcoin_info.png')" :alt="$t('products.altcoin.seo.whatAre.imageAlt')" class="w-auto rounded-lg shadow-lg" />
                        </a>
                    </div>
                </div>

                <!-- Was ist eine Altcoin-Season -->
                <div class="mb-20 lg:mb-0 lg:p-20 flex flex-col-reverse lg:flex-row items-center gap-8">
                    <div class="lg:w-1/2 m-auto flex-col justify-center">
                        <h2>{{ $t('products.altcoin.seo.season.title') }}</h2>
                        <p class="text-slate-600">
                            {{ $t('products.altcoin.seo.season.p1.beforeLink') }}
                            <router-link to="/hkcmnews/was-ist-eine-altcoin-season-und-wann-kommt-sie" class="underline underline-offset-2 font-bold text-blue" target="_blank">
                                {{ $t('products.altcoin.seo.season.linkText') }}
                            </router-link>
                            {{ $t('products.altcoin.seo.season.p1.afterLink') }}
                            <br /><br />
                            {{ $t('products.altcoin.seo.season.p2.beforeLink') }}
                            <router-link to="/hkcmnews/was-ist-eine-altcoin-season-und-wann-kommt-sie" class="underline underline-offset-2 font-bold text-blue" target="_blank">
                                {{ $t('products.altcoin.seo.season.linkText') }}
                            </router-link>
                            {{ $t('products.altcoin.seo.season.p2.afterLink') }}
                        </p>
                    </div>
                    <div class="lg:w-1/2 m-auto flex justify-center">
                        <img :src="$asset('/hkcm/images/altcoin_season.jpg')" :alt="$t('products.altcoin.seo.season.imageAlt')" class="w-auto rounded-lg shadow-lg" />
                    </div>
                </div>

                <!-- Was ist die Altcoin-Dominanz -->
                <div
                    class="bg-gradient-to-b from-gray-100 to-gray-200 rounded-lg shadow-inner mb-20 lg:mb-0 p-5 sm:p-10 lg:p-20 flex flex-col-reverse lg:flex-row-reverse items-center gap-8"
                >
                    <div class="lg:w-1/2 m-auto flex-col justify-center">
                        <h2>{{ $t('products.altcoin.seo.dominance.title') }}</h2>
                        <p class="text-slate-600" v-html="$t('products.altcoin.seo.dominance.text')"></p>
                    </div>
                    <div class="lg:w-1/2 m-auto flex justify-center">
                        <img :src="$asset('/hkcm/images/altcoin_dominance.jpg')" :alt="$t('products.altcoin.seo.dominance.imageAlt')" class="w-auto rounded-lg shadow-lg" />
                    </div>
                </div>
            </div>
        </section>

        <!-- Additional SEO Sections -->
        <section class="mb-20 lg:mb-0 bg-slate-50">
            <div class="max-w-6xl mx-auto px-4 py-12 lg:py-20">
                <h2 class="text-2xl lg:text-3xl font-bold text-slate-900 text-center mb-6">
                    {{ $t('products.altcoin.seo.seasonIndex.title') }}
                </h2>
                <p class="text-slate-600 leading-relaxed text-sm lg:text-base" v-html="$t('products.altcoin.seo.seasonIndex.text')"></p>
            </div>
        </section>

        <section class="mb-20 lg:mb-0 p-5 sm:p-10 lg:p-20 bg-gradient-to-b from-gray-100 to-gray-200 rounded-lg shadow-inner text-left">
            <div class="max-w-6xl mx-auto px-4 py-12 lg:py-20">
                <h2 class="text-2xl lg:text-3xl font-bold text-slate-900 text-center mb-6">
                    {{ $t('products.altcoin.seo.news.title') }}
                </h2>
                <p class="text-slate-600 leading-relaxed text-sm lg:text-base">
                    {{ $t('products.altcoin.seo.news.p1.beforeLink') }}
                    <router-link to="/hkcmnews" class="underline underline-offset-2 text-blue font-bold" target="_blank">
                        {{ $t('products.altcoin.seo.news.linkText') }}
                    </router-link>
                    {{ $t('products.altcoin.seo.news.p1.afterLink') }}
                    <br /><br />
                    {{ $t('products.altcoin.seo.news.p2') }}
                </p>
            </div>
        </section>

        <section class="mb-20 lg:mb-0 bg-slate-50">
            <div class="max-w-6xl mx-auto px-4 py-12 lg:py-20">
                <h2 class="text-2xl lg:text-3xl font-bold text-slate-900 text-center mb-6">
                    {{ $t('products.altcoin.seo.whichToBuy.title') }}
                </h2>
                <p class="text-slate-600 leading-relaxed text-sm lg:text-base" v-html="$t('products.altcoin.seo.whichToBuy.text')"></p>
            </div>
        </section>

        <section class="mb-20 lg:mb-0 p-5 sm:p-10 lg:p-20 bg-gradient-to-b from-gray-100 to-gray-200 rounded-lg shadow-inner text-left">
            <div class="max-w-6xl mx-auto px-4 py-12 lg:py-20">
                <h2 class="text-2xl lg:text-3xl font-bold text-slate-900 text-center mb-6">
                    {{ $t('products.altcoin.seo.blockchain.title') }}
                </h2>
                <p class="text-slate-600 leading-relaxed text-sm lg:text-base" v-html="$t('products.altcoin.seo.blockchain.text')"></p>
            </div>
        </section>

        <section class="mb-20 lg:mb-0 bg-slate-50">
            <div class="max-w-6xl mx-auto px-4 py-12 lg:py-20">
                <h2 class="text-2xl lg:text-3xl font-bold text-slate-900 text-center mb-6">
                    {{ $t('products.altcoin.seo.coinVsToken.title') }}
                </h2>
                <p class="text-slate-600 leading-relaxed text-sm lg:text-base" v-html="$t('products.altcoin.seo.coinVsToken.text')"></p>
            </div>
        </section>

        <!-- Floating CTA Button -->
        <FloatingCTAButton :is-visible="pricingCardInView" :text="$t('products.altcoin.floatingCta.text')" target-id="#pricing-card" />
    </div>
</template>

<script>
import ProductIntroSection from './ProductIntroSection.vue';
import HowItWorksSection from './HowItWorksSection.vue';
import ProductListSection from './ProductListSection.vue';
import ProductAnalysisSection from './ProductAnalysisSection.vue';
import FloatingCTAButton from './FloatingCTAButton.vue';
import ShopifyProduct from '../miscellaneous/ShopifyProducts.vue';
import Accordeon from '../miscellaneous/Accordeon.vue';
import altcoinData from '../../data/altcoin_testdaten.json';
import { fetchData } from '../../fetchData.js';

export default {
    name: 'Altcoin',
    components: {
        ProductIntroSection,
        HowItWorksSection,
        ProductListSection,
        ProductAnalysisSection,
        FloatingCTAButton,
        ShopifyProduct,
        Accordeon
    },
    props: {
        productGroup: {
            type: Object,
            required: true
        },
        selectedProductVariant: {
            type: Object,
            default: null
        },
        sortedProductVariants: {
            type: Array,
            default: () => []
        }
    },
    data: () => ({
        pricingCardInView: false,
        altcoinData,

        introFeatureDefs: [
            {
                titleKey: 'products.altcoin.intro.features.updatedDaily.title',
                descKey: 'products.altcoin.intro.features.updatedDaily.desc'
            },
            {
                titleKey: 'products.altcoin.intro.features.videoUpgrade.title',
                descKey: 'products.altcoin.intro.features.videoUpgrade.desc'
            },
            {
                titleKey: 'products.altcoin.intro.features.clearTrading.title',
                descKey: 'products.altcoin.intro.features.clearTrading.desc'
            },
            {
                titleKey: 'products.altcoin.intro.features.btcEthIncluded.title',
                descKey: 'products.altcoin.intro.features.btcEthIncluded.desc'
            }
        ],

        howItWorksCardDefs: [
            {
                titleKey: 'products.altcoin.howItWorks.cards.dailyWritten.title',
                textKey: 'products.altcoin.howItWorks.cards.dailyWritten.text',
                iconClass: 'fi fi-rr-envelope-open-text'
            },
            {
                titleKey: 'products.altcoin.howItWorks.cards.clearTrading.title',
                textKey: 'products.altcoin.howItWorks.cards.clearTrading.text',
                iconClass: 'fi fi-rr-chart-line-up'
            },
            {
                titleKey: 'products.altcoin.howItWorks.cards.chartImages.title',
                textKey: 'products.altcoin.howItWorks.cards.chartImages.text',
                iconClass: 'fi fi-rr-bolt'
            },
            {
                titleKey: 'products.altcoin.howItWorks.cards.fastReactions.title',
                textKey: 'products.altcoin.howItWorks.cards.fastReactions.text',
                iconClass: 'fi fi-rr-medal'
            },
            {
                titleKey: 'products.altcoin.howItWorks.cards.videoOptional.title',
                textKey: 'products.altcoin.howItWorks.cards.videoOptional.text',
                iconClass: 'fi fi-rr-video-camera'
            },
            {
                titleKey: 'products.altcoin.howItWorks.cards.weeklyCycle.title',
                textKey: 'products.altcoin.howItWorks.cards.weeklyCycle.text',
                iconClass: 'fi fi-rr-bullseye'
            }
        ],

        analysisFeatureDefs: [
            {
                titleKey: 'products.altcoin.analysis.features.structured.title',
                descKey: 'products.altcoin.analysis.features.structured.desc'
            },
            {
                titleKey: 'products.altcoin.analysis.features.targets.title',
                descKey: 'products.altcoin.analysis.features.targets.desc'
            },
            {
                titleKey: 'products.altcoin.analysis.features.transparency.title',
                descKey: 'products.altcoin.analysis.features.transparency.desc'
            }
        ],

        faqDefs: [
            {
                qKey: 'products.altcoin.faq.q.pricing.question',
                aKey: 'products.altcoin.faq.q.pricing.answer'
            },
            {
                qKey: 'products.altcoin.faq.q.included.question',
                aKey: 'products.altcoin.faq.q.included.answer'
            },
            {
                qKey: 'products.altcoin.faq.q.payment.question',
                aKey: 'products.altcoin.faq.q.payment.answer'
            },
            {
                qKey: 'products.altcoin.faq.q.app.question',
                aKey: 'products.altcoin.faq.q.app.answer'
            },
            {
                qKey: 'products.altcoin.faq.q.renewal.question',
                aKey: 'products.altcoin.faq.q.renewal.answer'
            },
            {
                qKey: 'products.altcoin.faq.q.aboutHkcm.question',
                aKey: 'products.altcoin.faq.q.aboutHkcm.answer'
            }
        ],

        _pricingCardObserver: null,

        dogeLogo: null,

        slpLogo: null
    }),

    computed: {
        introFeatures() {
            return this.introFeatureDefs.map((f) => ({
                title: this.$t(f.titleKey),
                desc: this.$t(f.descKey)
            }));
        },

        howItWorksDescription() {
            return this.$t('products.altcoin.howItWorks.description');
        },

        howItWorksCards() {
            return this.howItWorksCardDefs.map((c) => ({
                title: this.$t(c.titleKey),
                text: this.$t(c.textKey),
                iconClass: c.iconClass
            }));
        },

        analysisDescription() {
            return this.$t('products.altcoin.analysis.description');
        },

        analysisFeatures() {
            return this.analysisFeatureDefs.map((f) => ({
                title: this.$t(f.titleKey),
                desc: this.$t(f.descKey)
            }));
        },

        faqs() {
            return this.faqDefs.map((f) => ({
                question: this.$t(f.qKey),
                answer: this.$t(f.aKey)
            }));
        }
    },

    async mounted() {
        this.setupPricingObserver();

        this.dogeLogo = await this.fetchLogo('Doge');
        this.slpLogo = await this.fetchLogo('SLP');
    },
    beforeUnmount() {
        if (this._pricingCardObserver) {
            this._pricingCardObserver.disconnect();
            this._pricingCardObserver = null;
        }
    },
    methods: {
        setupPricingObserver() {
            const el = document.getElementById('pricing-card');

            if (el) {
                this._pricingCardObserver = new IntersectionObserver(
                    (entries) => {
                        const entry = entries[0];
                        this.pricingCardInView = entry.isIntersecting;
                    },
                    { root: null, threshold: 0.1 }
                );
                this._pricingCardObserver.observe(el);
            } else {
                this.$nextTick(() => {
                    const el2 = document.getElementById('pricing-card');

                    if (el2) {
                        this._pricingCardObserver = new IntersectionObserver(
                            (entries) => {
                                this.pricingCardInView = entries[0].isIntersecting;
                            },
                            { root: null, threshold: 0.1 }
                        );
                        this._pricingCardObserver.observe(el2);
                    }
                });
            }
        },
        async fetchLogo(symboleName) {
            try {
                const response = await fetchData(`/api/v1/market-symbols-logo/${symboleName}`);

                if (response && response.result && response.result.url) {
                    return response.result.url;
                } else {
                    console.error('Unexpected response structure:', response);

                    return null;
                }
            } catch (error) {
                console.error('Error fetching logo:', error);

                return null;
            }
        }
    }
};
</script>
